import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useForm } from 'react-hook-form';
import { usePermission } from '@/components/guards/permission-guard';
import {
  ArrowLeft,
  Store,
  Loader2,
  Trash2,
  Save,
  Plus,
  X,
  Star,
  Package,
} from 'lucide-react';
import { ProductImageManager } from '@/components/trading/product-image-manager';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { ConfirmationDialog } from '@/components/ui/confirmation-dialog';
import { trpc } from '@/lib/trpc';
import { formatCurrency } from '@/lib/utils';
import { applyPriceDiscount } from '@/lib/price-list';
import { showSuccessToast, showErrorToast } from '@/hooks/use-mutation-with-toast';

type StockStatus = 'IN_STOCK' | 'LOW_STOCK' | 'OUT_OF_STOCK' | 'PRE_ORDER' | 'DISCONTINUED';

interface Specification {
  key: string;
  value: string;
}

interface ProductFormData {
  modelId: string;
  code: string;
  sku?: string;
  name: string;
  slug: string;
  shortDescription?: string;
  description?: string;
  price?: number;
  currency: string;
  priceOnRequest: boolean;
  markupType?: string | null;
  markupValue?: number | null;
  kdvRate: number;
  vendorPrice?: number | null;
  vendorCurrency: string;
  vendorMarkupType?: string | null;
  vendorMarkupValue?: number | null;
  vendorKdvRate: number;
  stockStatus: StockStatus;
  minOrderQty: number;
  isActive: boolean;
  isFeatured: boolean;
  sortOrder: number;
}

export function TradingProductDetailPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { t } = useTranslation('trading');
  const { t: tCommon } = useTranslation('common');
  const canEdit = usePermission('PRODUCTS', 'EDIT');
  const canDelete = usePermission('PRODUCTS', 'DELETE');

  const [specifications, setSpecifications] = useState<Specification[]>([]);
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [selectedBrandId, setSelectedBrandId] = useState<string>('');

  const {
    register,
    handleSubmit,
    formState: { errors, isDirty },
    reset,
    setValue,
    watch,
  } = useForm<ProductFormData>();

  const { data: product, isLoading, error } = trpc.tradingProduct.byId.useQuery(
    id!,
    { enabled: !!id }
  );

  const { data: brandsData } = trpc.tradingBrand.list.useQuery({});
  const brands = brandsData?.brands ?? [];

  const { data: modelsData } = trpc.tradingModel.list.useQuery(
    { brandId: selectedBrandId },
    { enabled: !!selectedBrandId }
  );
  const models = modelsData?.models ?? [];

  const utils = trpc.useUtils();

  const updateMutation = trpc.tradingProduct.update.useMutation({
    onSuccess: () => {
      utils.tradingProduct.list.invalidate();
      utils.tradingProduct.byId.invalidate(id!);
      showSuccessToast(t('common.updated'));
    },
    onError: (error) => {
      showErrorToast(error.message);
    },
  });

  const deleteMutation = trpc.tradingProduct.delete.useMutation({
    onSuccess: () => {
      utils.tradingProduct.list.invalidate();
      utils.tradingModel.list.invalidate();
      showSuccessToast(t('common.deleted'));
      navigate('/trading/products');
    },
    onError: (error) => {
      showErrorToast(error.message);
    },
  });

  const watchPriceOnRequest = watch('priceOnRequest');
  const watchMarkupType = watch('markupType');
  const watchVendorMarkupType = watch('vendorMarkupType');

  useEffect(() => {
    if (product) {
      if (product.model?.brand?.id) {
        setSelectedBrandId(product.model.brand.id);
      }

      const toNum = (v: unknown): number | undefined => {
        if (v == null) return undefined;
        if (typeof v === 'object' && v !== null && 'toNumber' in v) {
          return (v as { toNumber: () => number }).toNumber();
        }
        return Number(v);
      };

      const price = toNum(product.price);
      const p = product as any;

      reset({
        modelId: product.modelId,
        code: product.code,
        sku: product.sku || '',
        name: product.name,
        slug: product.slug,
        shortDescription: product.shortDescription || '',
        description: product.description || '',
        price,
        currency: product.currency,
        priceOnRequest: product.priceOnRequest,
        markupType: p.markupType || null,
        markupValue: toNum(p.markupValue) ?? null,
        kdvRate: toNum(p.kdvRate) ?? 20,
        vendorPrice: toNum(p.vendorPrice) ?? null,
        vendorCurrency: p.vendorCurrency || 'EUR',
        vendorMarkupType: p.vendorMarkupType || null,
        vendorMarkupValue: toNum(p.vendorMarkupValue) ?? null,
        vendorKdvRate: toNum(p.vendorKdvRate) ?? 20,
        stockStatus: product.stockStatus as StockStatus,
        minOrderQty: product.minOrderQty,
        isActive: product.isActive,
        isFeatured: product.isFeatured,
        sortOrder: product.sortOrder,
      });

      if (product.specifications && typeof product.specifications === 'object' && !Array.isArray(product.specifications)) {
        setSpecifications(
          Object.entries(product.specifications as Record<string, string>).map(([key, value]) => ({
            key,
            value: String(value),
          }))
        );
      } else {
        setSpecifications([]);
      }
    }
  }, [product, reset]);

  useEffect(() => {
    if (product && models.length > 0) {
      const modelExists = models.some((m) => m.id === product.modelId);
      if (modelExists) {
        setValue('modelId', product.modelId);
      }
    }
  }, [product, models, setValue]);

  const addSpecification = () => {
    setSpecifications([...specifications, { key: '', value: '' }]);
  };

  const removeSpecification = (index: number) => {
    setSpecifications(specifications.filter((_, i) => i !== index));
  };

  const updateSpecification = (index: number, field: 'key' | 'value', value: string) => {
    const updated = [...specifications];
    const spec = updated[index];
    if (spec) {
      spec[field] = value;
      setSpecifications(updated);
    }
  };

  const onSubmit = (data: ProductFormData) => {
    if (!product) return;

    const specsObject = specifications.reduce((acc, spec) => {
      if (spec.key.trim()) {
        acc[spec.key.trim()] = spec.value;
      }
      return acc;
    }, {} as Record<string, string>);

    const payload = {
      modelId: data.modelId,
      code: data.code,
      name: data.name,
      slug: data.slug,
      currency: data.currency,
      priceOnRequest: data.priceOnRequest,
      markupType: (data.markupType as 'FLAT' | 'PERCENTAGE') || null,
      markupValue: data.markupType ? data.markupValue : null,
      kdvRate: data.kdvRate ?? 0,
      vendorCurrency: data.vendorCurrency,
      vendorMarkupType: (data.vendorMarkupType as 'FLAT' | 'PERCENTAGE') || null,
      vendorMarkupValue: data.vendorMarkupType ? data.vendorMarkupValue : null,
      vendorKdvRate: data.vendorKdvRate ?? 0,
      vendorPrice: data.vendorMarkupType ? undefined : data.vendorPrice,
      stockStatus: data.stockStatus,
      minOrderQty: data.minOrderQty,
      isActive: data.isActive,
      isFeatured: data.isFeatured,
      sortOrder: data.sortOrder,
      sku: data.sku || undefined,
      shortDescription: data.shortDescription || undefined,
      description: data.description || undefined,
      price: data.priceOnRequest ? undefined : data.price,
      specifications: Object.keys(specsObject).length > 0 ? specsObject : undefined,
    };

    updateMutation.mutate({ id: product.id, data: payload });
  };

  const confirmDelete = () => {
    if (product) {
      deleteMutation.mutate(product.id);
    }
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
        <span className="ml-2 text-sm text-muted-foreground">{tCommon('loading')}</span>
      </div>
    );
  }

  if (error || !product) {
    return (
      <div className="space-y-4">
        <Button variant="ghost" className="h-8" onClick={() => navigate('/trading/products')}>
          <ArrowLeft className="mr-2 h-4 w-4" />
          {t('products.backToList')}
        </Button>
        <div className="border border-red-200 bg-red-50 p-3 text-sm text-red-700">
          <p className="font-medium">{tCommon('error')}</p>
          <p>{error?.message || t('products.notFound')}</p>
        </div>
      </div>
    );
  }

  const primaryImage = product.images?.find((img) => img.isPrimary) || product.images?.[0];
  const isSubmitting = updateMutation.isPending;

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <Button variant="ghost" className="h-8" onClick={() => navigate('/trading/products')}>
            <ArrowLeft className="mr-2 h-4 w-4" />
            {t('products.backToList')}
          </Button>
          <div className="h-4 w-px bg-border" />
          <div className="flex items-center gap-2">
            {primaryImage ? (
              <img
                src={primaryImage.url}
                alt={product.name}
                className="h-8 w-8 object-cover"
                referrerPolicy="no-referrer"
              />
            ) : (
              <div className="h-8 w-8 bg-gray-100 flex items-center justify-center">
                <Store className="h-4 w-4 text-gray-400" />
              </div>
            )}
            <div>
              <h1 className="text-lg font-semibold">{product.name}</h1>
              <p className="text-xs text-muted-foreground font-mono">{product.fullCode}</p>
            </div>
          </div>
        </div>
        <div className="flex items-center gap-2">
          {canDelete && (
            <Button
              variant="outline"
              className="h-8 text-red-600 hover:text-red-700"
              onClick={() => setDeleteConfirmOpen(true)}
            >
              <Trash2 className="mr-2 h-4 w-4" />
              {tCommon('delete')}
            </Button>
          )}
          {canEdit && (
            <Button
              className="h-8"
              onClick={handleSubmit(onSubmit)}
              disabled={isSubmitting || !isDirty}
            >
              {isSubmitting ? (
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              ) : (
                <Save className="mr-2 h-4 w-4" />
              )}
              {tCommon('save')}
            </Button>
          )}
        </div>
      </div>

      {/* Delete Confirmation */}
      <ConfirmationDialog
        open={deleteConfirmOpen}
        onOpenChange={setDeleteConfirmOpen}
        title={tCommon('delete')}
        description={t('products.deleteConfirm')}
        confirmLabel={tCommon('delete')}
        variant="destructive"
        onConfirm={confirmDelete}
        isLoading={deleteMutation.isPending}
      />

      <form className="grid gap-4 lg:grid-cols-3">
        {/* Main Content */}
        <div className="lg:col-span-2 space-y-4">
          {/* Basic Info */}
          <div className="border border-border p-4 space-y-4">
            <h2 className="text-sm font-medium">{t('products.basicInfo')}</h2>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-1.5">
                <Label className="text-xs">{t('products.brand')}</Label>
                <select
                  value={selectedBrandId}
                  onChange={(e) => {
                    setSelectedBrandId(e.target.value);
                    setValue('modelId', '');
                  }}
                  disabled={!canEdit}
                  className="h-8 w-full border border-border bg-background px-2 text-sm focus:border-foreground focus:outline-none focus:ring-1 focus:ring-foreground/20 disabled:opacity-50"
                >
                  <option value="">{t('products.selectBrand')}</option>
                  {brands.map((brand) => (
                    <option key={brand.id} value={brand.id}>
                      {brand.code} - {brand.name}
                    </option>
                  ))}
                </select>
              </div>

              <div className="space-y-1.5">
                <Label className="text-xs">{t('products.model')}</Label>
                <select
                  {...register('modelId', { required: t('common.required') })}
                  disabled={!canEdit || !selectedBrandId}
                  className="h-8 w-full border border-border bg-background px-2 text-sm focus:border-foreground focus:outline-none focus:ring-1 focus:ring-foreground/20 disabled:opacity-50"
                >
                  <option value="">{t('products.selectModel')}</option>
                  {models.map((model) => (
                    <option key={model.id} value={model.id}>
                      {model.code} - {model.name}
                    </option>
                  ))}
                </select>
                {errors.modelId && (
                  <p className="text-xs text-red-500">{errors.modelId.message}</p>
                )}
              </div>
            </div>

            <div className="grid grid-cols-3 gap-4">
              <div className="space-y-1.5">
                <Label className="text-xs">{t('products.code')}</Label>
                <input
                  {...register('code', { required: t('common.required') })}
                  disabled
                  className="h-8 w-full border border-border bg-muted px-2 text-sm"
                />
              </div>
              <div className="space-y-1.5">
                <Label className="text-xs">{t('products.sku')}</Label>
                <input
                  {...register('sku')}
                  disabled={!canEdit}
                  className="h-8 w-full border border-border bg-background px-2 text-sm focus:border-foreground focus:outline-none focus:ring-1 focus:ring-foreground/20 disabled:opacity-50"
                />
              </div>
              <div className="space-y-1.5">
                <Label className="text-xs">{t('products.sortOrder')}</Label>
                <input
                  type="number"
                  {...register('sortOrder', { valueAsNumber: true })}
                  disabled={!canEdit}
                  className="h-8 w-full border border-border bg-background px-2 text-sm focus:border-foreground focus:outline-none focus:ring-1 focus:ring-foreground/20 disabled:opacity-50"
                />
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-1.5">
                <Label className="text-xs">{t('products.name')}</Label>
                <input
                  {...register('name', { required: t('common.required') })}
                  disabled={!canEdit}
                  className="h-8 w-full border border-border bg-background px-2 text-sm focus:border-foreground focus:outline-none focus:ring-1 focus:ring-foreground/20 disabled:opacity-50"
                />
                {errors.name && (
                  <p className="text-xs text-red-500">{errors.name.message}</p>
                )}
              </div>
              <div className="space-y-1.5">
                <Label className="text-xs">{t('products.slug')}</Label>
                <input
                  {...register('slug', { required: t('common.required') })}
                  disabled={!canEdit}
                  className="h-8 w-full border border-border bg-background px-2 text-sm focus:border-foreground focus:outline-none focus:ring-1 focus:ring-foreground/20 disabled:opacity-50"
                />
                {errors.slug && (
                  <p className="text-xs text-red-500">{errors.slug.message}</p>
                )}
              </div>
            </div>

            <div className="space-y-1.5">
              <Label className="text-xs">{t('products.shortDescription')}</Label>
              <input
                {...register('shortDescription')}
                disabled={!canEdit}
                className="h-8 w-full border border-border bg-background px-2 text-sm focus:border-foreground focus:outline-none focus:ring-1 focus:ring-foreground/20 disabled:opacity-50"
              />
            </div>

            <div className="space-y-1.5">
              <Label className="text-xs">{t('products.description')}</Label>
              <textarea
                {...register('description')}
                rows={4}
                disabled={!canEdit}
                className="w-full border border-border bg-background px-2 py-1.5 text-sm focus:border-foreground focus:outline-none focus:ring-1 focus:ring-foreground/20 disabled:opacity-50 resize-none"
              />
            </div>
          </div>

          {/* Pricing — retail + vendor share one card; the lot table at the
              bottom previews both auto-derived prices side-by-side. */}
          <div className="border border-border p-4 space-y-4">
            <h2 className="text-sm font-medium">{t('products.pricing')}</h2>

            {/* Retail row */}
            <div className="space-y-2">
              <h3 className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                {t('products.retailPrice')}
              </h3>
              <div className="grid grid-cols-4 gap-4">
                <div className="space-y-1.5">
                  <Label className="text-xs">{t('products.markupType')}</Label>
                  <select
                    {...register('markupType')}
                    disabled={!canEdit}
                    className="h-8 w-full border border-border bg-background px-2 text-sm focus:border-foreground focus:outline-none focus:ring-1 focus:ring-foreground/20 disabled:opacity-50"
                  >
                    <option value="">{t('products.noMarkup')}</option>
                    <option value="FLAT">{t('products.flatMarkup')}</option>
                    <option value="PERCENTAGE">{t('products.percentageMarkup')}</option>
                  </select>
                </div>
                <div className="space-y-1.5">
                  <Label className="text-xs">
                    {watchMarkupType === 'FLAT' ? t('products.markupAmount') : t('products.markupPercentage')}
                  </Label>
                  <input
                    type="number"
                    step="0.01"
                    min="0"
                    {...register('markupValue', { valueAsNumber: true })}
                    disabled={!canEdit || !watchMarkupType}
                    className="h-8 w-full border border-border bg-background px-2 text-sm focus:border-foreground focus:outline-none focus:ring-1 focus:ring-foreground/20 disabled:opacity-50"
                  />
                </div>
                <div className="space-y-1.5">
                  <Label className="text-xs">{t('products.kdvRate')}</Label>
                  <input
                    type="number"
                    step="0.01"
                    min="0"
                    max="100"
                    {...register('kdvRate', { valueAsNumber: true })}
                    disabled={!canEdit}
                    className="h-8 w-full border border-border bg-background px-2 text-sm focus:border-foreground focus:outline-none focus:ring-1 focus:ring-foreground/20 disabled:opacity-50"
                  />
                </div>
                <div className="space-y-1.5">
                  <Label className="text-xs">{t('products.currency')}</Label>
                  <select
                    {...register('currency')}
                    disabled={!canEdit}
                    className="h-8 w-full border border-border bg-background px-2 text-sm focus:border-foreground focus:outline-none focus:ring-1 focus:ring-foreground/20 disabled:opacity-50"
                  >
                    <option value="EUR">EUR</option>
                    <option value="USD">USD</option>
                    <option value="GBP">GBP</option>
                    <option value="TRY">TRY</option>
                  </select>
                </div>
              </div>

              {/* Manual retail override */}
              <div className="flex items-center gap-4 pt-1">
                <label className="flex items-center gap-2 text-sm">
                  <input
                    type="checkbox"
                    {...register('priceOnRequest')}
                    disabled={!canEdit}
                    className="h-4 w-4 border-border"
                  />
                  {t('products.priceOnRequest')}
                </label>
                <span className="text-xs text-muted-foreground">
                  {watchMarkupType
                    ? t('products.priceAutoFromFifo')
                    : t('products.priceManualHint')}
                </span>
              </div>

              {!watchPriceOnRequest && (
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-1.5">
                    <Label className="text-xs">{t('products.price')}</Label>
                    <input
                      type="number"
                      step="0.01"
                      {...register('price', { valueAsNumber: true })}
                      disabled={!canEdit || !!watchMarkupType}
                      className="h-8 w-full border border-border bg-background px-2 text-sm focus:border-foreground focus:outline-none focus:ring-1 focus:ring-foreground/20 disabled:opacity-50"
                    />
                  </div>
                </div>
              )}
            </div>

            {/* Vendor row */}
            <div className="border-t border-border pt-4 space-y-2">
              <h3 className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                {t('products.vendorPricing')}
              </h3>
              <div className="grid grid-cols-4 gap-4">
                <div className="space-y-1.5">
                  <Label className="text-xs">{t('products.markupType')}</Label>
                  <select
                    {...register('vendorMarkupType')}
                    disabled={!canEdit}
                    className="h-8 w-full border border-border bg-background px-2 text-sm focus:border-foreground focus:outline-none focus:ring-1 focus:ring-foreground/20 disabled:opacity-50"
                  >
                    <option value="">{t('products.noMarkup')}</option>
                    <option value="FLAT">{t('products.flatMarkup')}</option>
                    <option value="PERCENTAGE">{t('products.percentageMarkup')}</option>
                  </select>
                </div>
                <div className="space-y-1.5">
                  <Label className="text-xs">
                    {watchVendorMarkupType === 'FLAT' ? t('products.markupAmount') : t('products.markupPercentage')}
                  </Label>
                  <input
                    type="number"
                    step="0.01"
                    min="0"
                    {...register('vendorMarkupValue', { valueAsNumber: true })}
                    disabled={!canEdit || !watchVendorMarkupType}
                    className="h-8 w-full border border-border bg-background px-2 text-sm focus:border-foreground focus:outline-none focus:ring-1 focus:ring-foreground/20 disabled:opacity-50"
                  />
                </div>
                <div className="space-y-1.5">
                  <Label className="text-xs">{t('products.kdvRate')}</Label>
                  <input
                    type="number"
                    step="0.01"
                    min="0"
                    max="100"
                    {...register('vendorKdvRate', { valueAsNumber: true })}
                    disabled={!canEdit}
                    className="h-8 w-full border border-border bg-background px-2 text-sm focus:border-foreground focus:outline-none focus:ring-1 focus:ring-foreground/20 disabled:opacity-50"
                  />
                </div>
                <div className="space-y-1.5">
                  <Label className="text-xs">{t('products.currency')}</Label>
                  <select
                    {...register('vendorCurrency')}
                    disabled={!canEdit}
                    className="h-8 w-full border border-border bg-background px-2 text-sm focus:border-foreground focus:outline-none focus:ring-1 focus:ring-foreground/20 disabled:opacity-50"
                  >
                    <option value="EUR">EUR</option>
                    <option value="USD">USD</option>
                    <option value="GBP">GBP</option>
                    <option value="TRY">TRY</option>
                  </select>
                </div>
              </div>

              {!watchVendorMarkupType && (
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-1.5">
                    <Label className="text-xs">{t('products.vendorPrice')}</Label>
                    <input
                      type="number"
                      step="0.01"
                      {...register('vendorPrice', { valueAsNumber: true })}
                      disabled={!canEdit}
                      className="h-8 w-full border border-border bg-background px-2 text-sm focus:border-foreground focus:outline-none focus:ring-1 focus:ring-foreground/20 disabled:opacity-50"
                    />
                  </div>
                </div>
              )}
            </div>

            {/* Lot table — shared FIFO preview for both retail and vendor prices */}
            {product && (
              <div className="border-t border-border pt-4">
                <LotPricingTable
                  productId={product.id}
                  retail={{
                    currency: watch('currency') || 'EUR',
                    markupType: watchMarkupType as 'FLAT' | 'PERCENTAGE' | undefined,
                    markupValue: watch('markupValue') ?? undefined,
                    kdvRate: watch('kdvRate') ?? 0,
                    discountPercent: Number(product.discountPercent ?? 0),
                  }}
                  vendor={{
                    currency: watch('vendorCurrency') || 'EUR',
                    markupType: watchVendorMarkupType as 'FLAT' | 'PERCENTAGE' | undefined,
                    markupValue: watch('vendorMarkupValue') ?? undefined,
                    kdvRate: watch('vendorKdvRate') ?? 0,
                    discountPercent: Number(product.vendorDiscountPercent ?? 0),
                  }}
                />
              </div>
            )}
          </div>

          {/* Stock & Order Settings */}
          <div className="border border-border p-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-1.5">
                <Label className="text-xs">{t('products.stockStatus')}</Label>
                <select
                  {...register('stockStatus')}
                  disabled={!canEdit}
                  className="h-8 w-full border border-border bg-background px-2 text-sm focus:border-foreground focus:outline-none focus:ring-1 focus:ring-foreground/20 disabled:opacity-50"
                >
                  {/* IN_STOCK is the "auto" default — the badge is derived
                      from live StockLevel aggregates. PRE_ORDER and
                      DISCONTINUED are sticky manual overrides. */}
                  <option value="IN_STOCK">{t('products.stockAuto')}</option>
                  <option value="PRE_ORDER">{t('products.preOrder')}</option>
                  <option value="DISCONTINUED">{t('products.discontinued')}</option>
                </select>
                <p className="text-[11px] text-muted-foreground">
                  {t('products.stockStatusHint')}
                </p>
              </div>
              <div className="space-y-1.5">
                <Label className="text-xs">{t('products.minOrderQty')}</Label>
                <input
                  type="number"
                  min={1}
                  {...register('minOrderQty', { valueAsNumber: true })}
                  disabled={!canEdit}
                  className="h-8 w-full border border-border bg-background px-2 text-sm focus:border-foreground focus:outline-none focus:ring-1 focus:ring-foreground/20 disabled:opacity-50"
                />
              </div>
            </div>
          </div>

          {/* Specifications */}
          <div className="border border-border p-4 space-y-4">
            <div className="flex items-center justify-between">
              <h2 className="text-sm font-medium">{t('products.specifications')}</h2>
              {canEdit && (
                <Button
                  type="button"
                  variant="outline"
                  className="h-7 text-xs"
                  onClick={addSpecification}
                >
                  <Plus className="mr-1 h-3 w-3" />
                  {t('products.addSpec')}
                </Button>
              )}
            </div>

            {specifications.length === 0 ? (
              <p className="text-sm text-muted-foreground">{t('products.noSpecs')}</p>
            ) : (
              <div className="space-y-2">
                {specifications.map((spec, index) => (
                  <div key={index} className="flex gap-2">
                    <input
                      placeholder={t('products.specKey')}
                      value={spec.key}
                      onChange={(e) => updateSpecification(index, 'key', e.target.value)}
                      disabled={!canEdit}
                      className="h-8 flex-1 border border-border bg-background px-2 text-sm focus:border-foreground focus:outline-none focus:ring-1 focus:ring-foreground/20 disabled:opacity-50"
                    />
                    <input
                      placeholder={t('products.specValue')}
                      value={spec.value}
                      onChange={(e) => updateSpecification(index, 'value', e.target.value)}
                      disabled={!canEdit}
                      className="h-8 flex-1 border border-border bg-background px-2 text-sm focus:border-foreground focus:outline-none focus:ring-1 focus:ring-foreground/20 disabled:opacity-50"
                    />
                    {canEdit && (
                      <Button
                        type="button"
                        variant="ghost"
                        size="icon"
                        className="h-8 w-8 text-red-500 hover:text-red-600"
                        onClick={() => removeSpecification(index)}
                      >
                        <X className="h-4 w-4" />
                      </Button>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>

        {/* Sidebar */}
        <div className="space-y-4">
          {/* Status */}
          <div className="border border-border p-4 space-y-4">
            <h2 className="text-sm font-medium">{t('products.status')}</h2>

            <div className="space-y-3">
              <label className="flex items-center gap-2 text-sm">
                <input
                  type="checkbox"
                  {...register('isActive')}
                  disabled={!canEdit}
                  className="h-4 w-4 border-border"
                />
                {t('products.active')}
              </label>

              <label className="flex items-center gap-2 text-sm">
                <input
                  type="checkbox"
                  {...register('isFeatured')}
                  disabled={!canEdit}
                  className="h-4 w-4 border-border"
                />
                <Star className="h-4 w-4 text-yellow-500" />
                {t('products.featured')}
              </label>
            </div>
          </div>

          {/* Images */}
          <div className="border border-border p-4 space-y-4">
            <h2 className="text-sm font-medium">{t('products.images')}</h2>
            <ProductImageManager
              productId={product.id}
              images={product.images ?? []}
            />
          </div>

          {/* Metadata */}
          <div className="border border-border p-4 space-y-2 text-xs">
            <div className="flex justify-between">
              <span className="text-muted-foreground">{t('products.created')}</span>
              <span>{new Date(product.createdAt).toLocaleDateString()}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-muted-foreground">{t('products.updated')}</span>
              <span>{new Date(product.updatedAt).toLocaleDateString()}</span>
            </div>
          </div>
        </div>
      </form>
    </div>
  );
}

interface PricingConfig {
  currency: string;
  markupType?: 'FLAT' | 'PERCENTAGE';
  markupValue?: number;
  kdvRate: number;
  discountPercent?: number;
}

// Lot-based pricing table — FIFO order, oldest available lot is the
// "active" one driving auto-derived prices (server recomputes on lot
// changes, this table is a live preview of the same math).
function LotPricingTable({
  productId,
  retail,
  vendor,
}: {
  productId: string;
  retail: PricingConfig;
  vendor: PricingConfig;
}) {
  const { t } = useTranslation('trading');
  const { data: batches, isLoading } = trpc.stockLevel.getProductBatches.useQuery(
    { productId, includeDepletedBatches: true },
    { enabled: !!productId }
  );

  if (isLoading) {
    return (
      <div className="flex items-center gap-2 text-sm text-muted-foreground py-2">
        <Loader2 className="h-3.5 w-3.5 animate-spin" />
      </div>
    );
  }

  if (!batches || batches.length === 0) {
    return (
      <div className="flex items-center gap-2 text-sm text-muted-foreground py-3 px-3 bg-muted/30 border border-border">
        <Package className="h-4 w-4" />
        {t('products.noLots')}
      </div>
    );
  }

  // Per-batch frozen FX from the server: rates as of the GRN's receipt date,
  // with the GRN's frozen PO-ccy→TRY rate taking precedence. Display values
  // therefore stay anchored to what was booked at receipt time and don't
  // drift when live FX moves.
  const sellPrice = (totalCost: number, cfg: PricingConfig): number | null => {
    if (!cfg.markupType || cfg.markupValue == null || isNaN(cfg.markupValue)) return null;
    const beforeKdv = cfg.markupType === 'FLAT'
      ? totalCost + cfg.markupValue
      : totalCost * (1 + cfg.markupValue / 100);
    const gross = beforeKdv * (1 + (cfg.kdvRate || 0) / 100);
    return cfg.discountPercent ? applyPriceDiscount(gross, cfg.discountPercent) : gross;
  };

  // Per-batch breakdown in retail and vendor currency.
  const rows = batches.map((batch) => {
    const batchCurrency = batch.poCurrency ?? batch.currency;
    const fx = (batch as any).frozenFxRates as Record<string, number> | undefined;

    // Convert using THIS batch's frozen FX. rates[X] = TRY per 1 X.
    const convert = (amount: number, from: string, to: string): number | null => {
      if (from === to) return amount;
      if (!fx) return null;
      const inTry = from === 'TRY' ? amount : (fx[from] ? amount * fx[from] : null);
      if (inTry == null) return null;
      if (to === 'TRY') return inTry;
      const toRate = fx[to];
      if (!toRate) return null;
      return inTry / toRate;
    };

    const buildFor = (cfg: PricingConfig) => {
      // GRN lots carry a PO-currency purchase leg. Non-GRN lots (adjustment,
      // transfer, initial stock) have none — their batch.unitCost IS the TRY
      // book value, priced via the receipt-date snapshot exactly like the
      // server-side recompute (reconstructLotCost anchors them to TRY).
      const purchase = batch.purchaseUnitCost != null
        ? convert(batch.purchaseUnitCost, batchCurrency, cfg.currency)
        : convert(batch.unitCost, 'TRY', cfg.currency);
      const landed = batch.landedCostPerUnit != null && batch.landedCostPerUnit > 0
        ? convert(batch.landedCostPerUnit, 'TRY', cfg.currency)
        : null;
      const total = purchase != null ? purchase + (landed ?? 0) : null;
      const sell = total != null ? sellPrice(total, cfg) : null;
      return { purchase, landed, total, sell };
    };
    return { ...batch, retailRow: buildFor(retail), vendorRow: buildFor(vendor) };
  });

  // Historical lots remain visible; only an available lot can drive FIFO pricing.
  const activeIndex = rows.findIndex((b) => b.status === 'AVAILABLE' && b.availableQty > 0);
  const active = activeIndex >= 0 ? rows[activeIndex] : null;
  const outOfStock = rows.every((b) => b.availableQty <= 0);

  return (
    <div className="space-y-3">
      {!!(retail.discountPercent || vendor.discountPercent) && (
        <p className="text-xs text-muted-foreground">
          {t('products.bulkDiscount.currentDiscounts', { retail: retail.discountPercent ?? 0, vendor: vendor.discountPercent ?? 0 })}
        </p>
      )}
      <div className="flex items-center justify-between">
        <Label className="text-xs font-medium">{t('products.lotPricing')}</Label>
        {outOfStock && (
          <span className="text-xs text-muted-foreground">{t('products.lotOutOfStock')}</span>
        )}
        {active && (
          <div className="text-xs text-muted-foreground">
            {t('products.fifoActiveHint')}{' '}
            <span className="font-mono">{active.batchNumber}</span>
          </div>
        )}
      </div>

      <div className="border border-border overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-muted/50">
            <tr>
              <th className="px-3 py-1.5 text-left text-xs font-medium w-20">{tStatus()}</th>
              <th className="px-3 py-1.5 text-left text-xs font-medium">{t('products.lotGrn')}</th>
              <th className="px-3 py-1.5 text-left text-xs font-medium">{t('products.supplier')}</th>
              <th className="px-3 py-1.5 text-right text-xs font-medium">{t('products.available')}</th>
              <th className="px-3 py-1.5 text-right text-xs font-medium">{t('products.purchase')}</th>
              <th className="px-3 py-1.5 text-right text-xs font-medium">{t('products.landed')}</th>
              <th className="px-3 py-1.5 text-right text-xs font-medium">{t('products.totalCost')}</th>
              {retail.markupType && (
                <th className="px-3 py-1.5 text-right text-xs font-medium">{t('products.retailPrice')}</th>
              )}
              {vendor.markupType && (
                <th className="px-3 py-1.5 text-right text-xs font-medium">{t('products.vendorPrice')}</th>
              )}
            </tr>
          </thead>
          <tbody className="divide-y">
            {rows.map((batch, i) => {
              const isActive = i === activeIndex;
              return (
                <tr
                  key={batch.id}
                  className={isActive ? 'bg-emerald-50/60' : 'hover:bg-muted/30'}
                >
                  <td className="px-3 py-1.5">
                    {isActive ? (
                      <span className="px-1.5 py-0.5 text-[10px] font-medium bg-emerald-100 text-emerald-700">
                        {t('products.fifoActive')}
                      </span>
                    ) : batch.availableQty <= 0 ? (
                      <span className="text-[10px] text-muted-foreground">
                        {t('products.lotOutOfStock')}
                      </span>
                    ) : (
                      <span className="text-[10px] text-muted-foreground">
                        {t('products.queued')}
                      </span>
                    )}
                  </td>
                  <td className="px-3 py-1.5">
                    <div className="font-mono text-xs">{batch.batchNumber}</div>
                    {batch.receiptNumber && (
                      <div className="text-[11px] text-muted-foreground">
                        {batch.receiptNumber}
                        {batch.poNumber && <span> / {batch.poNumber}</span>}
                      </div>
                    )}
                  </td>
                  <td className="px-3 py-1.5 text-xs">{batch.supplierName || '-'}</td>
                  <td className="px-3 py-1.5 text-right font-mono text-xs">{batch.availableQty}</td>
                  <td className="px-3 py-1.5 text-right font-mono text-xs">
                    {batch.retailRow.purchase != null
                      ? formatCurrency(batch.retailRow.purchase, retail.currency)
                      : '-'}
                  </td>
                  <td className="px-3 py-1.5 text-right font-mono text-xs">
                    {batch.retailRow.landed != null ? (
                      <span className="text-orange-700">
                        +{formatCurrency(batch.retailRow.landed, retail.currency)}
                      </span>
                    ) : '-'}
                  </td>
                  <td className="px-3 py-1.5 text-right font-mono text-xs font-medium">
                    {batch.retailRow.total != null
                      ? formatCurrency(batch.retailRow.total, retail.currency)
                      : <span className="text-muted-foreground">{t('products.needFxRate')}</span>}
                  </td>
                  {retail.markupType && (
                    <td className="px-3 py-1.5 text-right font-mono text-xs">
                      {batch.retailRow.sell != null ? (
                        <span className={`font-medium ${isActive ? 'text-emerald-700' : 'text-muted-foreground'}`}>
                          {formatCurrency(batch.retailRow.sell, retail.currency)}
                        </span>
                      ) : '-'}
                    </td>
                  )}
                  {vendor.markupType && (
                    <td className="px-3 py-1.5 text-right font-mono text-xs">
                      {batch.vendorRow.sell != null ? (
                        <span className={`font-medium ${isActive ? 'text-blue-700' : 'text-muted-foreground'}`}>
                          {formatCurrency(batch.vendorRow.sell, vendor.currency)}
                        </span>
                      ) : '-'}
                    </td>
                  )}
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {!retail.markupType && !vendor.markupType && (
        <p className="text-xs text-muted-foreground">{t('products.selectMarkupHint')}</p>
      )}
    </div>
  );

  // Locally-scoped helper to keep the header column compact.
  function tStatus() {
    return t('products.fifoStatus');
  }
}
